import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
    Alert,
    useTheme,
    useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DynamicJobForm from './DynamicJobForm';
import JobStatusTable from './JobStatusTable';
import { callApi } from '../utils/apiHelper';
import { validateAndTransformData } from '../utils/formValidator';
import jobParameterSchemas from '../config/jobParameterSchemas.json';
import { useJobParameters } from '../hooks/useJobParameters';

/**
 * JobCreationPage Component
 * New page component that replaces the old JobSubmission component
 * Provides a job type selector and renders DynamicJobForm based on selection
 */
const JobCreationPage = ({ mode, onNavigateBack }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State management
    const [selectedJobType, setSelectedJobType] = useState('attribute');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState(null);
    const [jobId, setJobId] = useState(null);
    const [refreshJobStatusTable, setRefreshJobStatusTable] = useState(0);

    // Use the useJobParameters hook
    const jobParametersHook = useJobParameters();

    const jobTypes = jobParameterSchemas.jobTypes;
    const currentJobSchema = jobTypes[selectedJobType];

    const cardStyle = {
        border: `1px solid ${mode === 'light' ? '#e0e0e0' : '#555'}`,
        borderRadius: '20px',
        backgroundColor: mode === 'light' ? '#fff' : '#2c2c2c',
        boxShadow: theme.shadows[2],
        transition: 'background-color 0.3s, border-color 0.3s'
    };

    // Handle job type change
    const handleJobTypeChange = (event) => {
        setSelectedJobType(event.target.value);
        setSubmitStatus(null);
        setSubmitMessage('');
        setSubmitError(null);
    };

    // Handle form submission
    const handleFormSubmit = async (formValues) => {
        setIsSubmitting(true);
        setSubmitStatus(null);
        setSubmitMessage('');
        setSubmitError(null);

        try {
            // Validate and transform data for API submission
            const validationResult = validateAndTransformData(selectedJobType, formValues);

            if (!validationResult.isValid) {
                setSubmitStatus('error');
                setSubmitError('Form validation failed');
                setIsSubmitting(false);
                return;
            }

            // Use the submitJob method from useJobParameters
            const response = await jobParametersHook.submitJob(async (params) => {
                return await callApi(params);
            }, validationResult.data);

            // Handle user cancellation
            if (response && response.cancelled) {
                // User cancelled the submission - gracefully return without showing error
                setIsSubmitting(false);
                return;
            }

            // Handle successful submission
            if (response && response.message) {
                setJobId(response.job_id || 'N/A');
                setSubmitStatus('success');
                setSubmitMessage(`Job submitted successfully! ${response.message}`);
                // Trigger job status table refresh
                setRefreshJobStatusTable(prev => prev + 1);
            } else {
                setSubmitStatus('error');
                setSubmitError('Unexpected response from server');
            }
        } catch (error) {
            console.error('Job submission error:', error);
            setSubmitStatus('error');
            setSubmitError(
                error.message || 'Failed to submit job. Please try again.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (onNavigateBack) {
            onNavigateBack();
        }
    };

    return (
        <Box sx={{
            width: '100%',
            marginTop: '1em',
            backgroundColor: mode === 'light' ? '#fff' : '#1e1e1e',
            color: mode === 'light' ? '#000' : '#fff',
            transition: 'background-color 0.3s, color 0.3s'
        }}>
            <Box
                sx={{
                    width: '100%',
                    margin: '0 auto',
                    padding: isMobile ? '1rem' : '2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isMobile ? '1rem' : '1.5rem'
                }}
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.primary.main
                        }}
                    >
                        Create New Job
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile ? '1rem' : '1.5rem'
                    }}
                >
                    <Card sx={{ ...cardStyle, padding: 0 }}>
                        <CardContent>
                            <Typography
                                variant="h6"
                                sx={{
                                    marginBottom: '1rem',
                                    fontWeight: 600
                                }}
                            >
                                Job Details
                            </Typography>

                            <FormControl fullWidth size="small" sx={{ marginBottom: '1rem' }}>
                                <InputLabel>Job Type</InputLabel>
                                <Select value={selectedJobType} onChange={handleJobTypeChange} label="Job Type">
                                    {Object.entries(jobTypes).map(([jobTypeKey, jobTypeData]) => (
                                        <MenuItem key={jobTypeKey} value={jobTypeKey}>
                                            {jobTypeData.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {currentJobSchema && (
                                <Box
                                    sx={{
                                        padding: '1rem',
                                        backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f9f9f9',
                                        borderRadius: '12px',
                                        borderLeft: `4px solid ${theme.palette.primary.main}`
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                                        {currentJobSchema.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        {currentJobSchema.description}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {submitStatus === 'success' && (
                        <Alert severity="success" icon={<CheckCircleIcon />}>
                            {submitMessage}
                        </Alert>
                    )}

                    {submitStatus === 'error' && (
                        <Alert severity="error" icon={<ErrorIcon />}>
                            {submitError}
                        </Alert>
                    )}

                    <Card sx={{ ...cardStyle, padding: 0 }}>
                        <CardContent>
                            <DynamicJobForm
                                jobType={selectedJobType}
                                onSubmit={handleFormSubmit}
                                isLoading={isSubmitting}
                                onCancel={handleCancel}
                            />
                        </CardContent>
                    </Card>

                    <Card sx={{ ...cardStyle, padding: 0 }}>
                        <CardContent>
                            <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                                Recent Job Status
                            </Typography>
                            <JobStatusTable refreshTrigger={refreshJobStatusTable} />
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};

export default JobCreationPage;
